import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import ChatContainer from "./components/ChatContainer";
import {
  MessageInputContainer,
  Input,
  SendButton,
} from "./components/MessageInput";
import MessagesContainer from "./components/MessagesContainer";
import Messages from "./components/Messages";
import Header from "./components/Header";
import Disclaimer from "./components/Disclaimer";
import OptIn from "./components/OptIn";
import Api from "./util/api";

let urlParams = new URLSearchParams(window.location.search);
let mode = urlParams.get("mode");

function App() {
  const [appState, setAppState] = useState("loading"); // ['disclaimer', 'chat'];
  const [channel, setChannel] = useState(mode); // ['web', 'sms']
  const [conversationId, setConversationId] = useState("");
  const [sending, setSending] = useState(false);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const [messages, setMessages] = useState([]);

  const [currentMessage, setCurrentMessage] = useState("");

  const sendMessage = useCallback(async () => {
    let newMessages = [...messages];
    if (currentMessage.trim()) {
      newMessages.push({ id: Date.now(), content: currentMessage, sent: true });
      setMessages(newMessages);
      setCurrentMessage("");
    }
    setSending(true);
    try {
      const request = { message: currentMessage };
      if (conversationId) {
        request.conversation_id = conversationId;
      }
      const response = await Api.post("/wellness-coach/conversation", request);
      setSending(false);
      if (response.data.response) {
        newMessages.push({
          id: response.data.message_id,
          content: response.data.response,
          sent: false,
        });
        setMessages(newMessages);
        setConversationId(response.data.conversation_id);
      }
      setTimeout(() => {
        inputRef.current.focus();
      }, 1000);
    } catch (e) {}
  }, [messages, conversationId, currentMessage]);

  const kickstart = async () => {
    try {
      const response = await Api.get("/wellness-coach/initial?mode=" + channel);
      if (response.data.initial) {
        setAppState("initial-loading");
        await Api.post("/wellness-coach/kickstart");
        setAppState("sms-start");
      } else {
        await Api.post("/wellness-coach/kickstart");
        setAppState("sms-start");
      }
    } catch (e) {
      setError(
        "We couldn't start your conversation. Please try again in a moment."
      );
      setAppState("initial-error");
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      // Function to scroll to the bottom
      const scrollToBottom = () => {
        container.scrollTop = container.scrollHeight;
      };

      // Create an observer to watch for changes in the container
      const observer = new MutationObserver(scrollToBottom);
      // Start observing for child-list changes
      observer.observe(container, { childList: true });

      // Perform an initial scroll in case there are already messages
      scrollToBottom();

      // Disconnect the observer on cleanup
      return () => observer.disconnect();
    }
  }, [messages]); // Trigger this effect whenever the messages change

  useEffect(() => {
    (async () => {
      try {
        const response = await Api.get("/profile").then((res) => {
          setPhone(res.data.phone);
        });
        setAppState("disclaimer");
      } catch (e) {
        setError(
          "No session active. Please launch the chat from the Freede platform."
        );
        setAppState("error");
      }
    })();
  }, []);

  useEffect(() => {
    const setVhVariable = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    // Set the variable when the component mounts
    setVhVariable();

    // Update the variable whenever the window is resized
    window.addEventListener("resize", setVhVariable);

    return () => {
      // Remove the event listener when the component unmounts
      window.removeEventListener("resize", setVhVariable);
    };
  }, []);

  const cancelChat = () => {
    window.close();
  };

  const handleInput = useCallback(
    (e) => {
      const lineHeight = 20; // height of one line of text in pixels
      const minHeight = 20; // minimum height in pixels
      const maxHeight = 60; // maximum height in pixels
      const textarea = e.target;
      // Calculate the number of lines
      const numberOfLines = Math.ceil(textarea.scrollHeight / lineHeight) - 1;
      // Calculate the new height based on the number of lines
      let newHeight = numberOfLines * lineHeight;

      // Ensure the new height is within the min and max bounds
      newHeight = Math.max(newHeight, minHeight);
      newHeight = Math.min(newHeight, maxHeight);

      textarea.style.height = `${newHeight}px`;

      if (e.key === "Enter" && !e.shiftKey) {
        sendMessage().then((r) => {});
      }
    },
    [sendMessage]
  );

  const startChat = useCallback(async () => {
    try {
      const initialCheck = await Api.get("/wellness-coach/initial?mode=" + channel);
      if (initialCheck.data.initial) {
        setAppState("initial-loading");
      } else {
        setAppState("chat");
        setSending(true);
      }
      const response = await Api.post("/wellness-coach/conversation", { message: "" });
      if (response.data.response) {
        setMessages([{
          id: response.data.message_id,
          content: response.data.response,
          sent: false,
        }]);
        setConversationId(response.data.conversation_id);
      }
      setSending(false);
      setAppState("chat");
    } catch (e) {
      setSending(false);
      setError(
        "We couldn't start your conversation. Please try again in a moment."
      );
      setAppState("initial-error");
    }
  }, [channel]);

  return (
    <div className="App">
      <ChatContainer>
        <Header />
        {appState === "initial-loading" && (
          <div
            style={{
              marginLeft: "auto",
              marginRight: "auto",
              textAlign: "center",
            }}
          >
            <p style={{ color: "white", margin: "50px", textAlign: "center" }}>
              Analyzing your profile and learning more about you. This may take
              a minute for the first conversation. <br />
              <br />
              Please wait...
            </p>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100px"
              height="100px"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid"
            >
              <g transform="translate(50 50)">
                <g>
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    values="0;45"
                    keyTimes="0;1"
                    dur="1.5873015873015872s"
                    repeatCount="indefinite"
                  ></animateTransform>
                  <path
                    d="M29.491524206117255 -5.5 L37.491524206117255 -5.5 L37.491524206117255 5.5 L29.491524206117255 5.5 A30 30 0 0 1 24.742744050198738 16.964569457146712 L24.742744050198738 16.964569457146712 L30.399598299691117 22.621423706639092 L22.621423706639096 30.399598299691114 L16.964569457146716 24.742744050198734 A30 30 0 0 1 5.5 29.491524206117255 L5.5 29.491524206117255 L5.5 37.491524206117255 L-5.499999999999997 37.491524206117255 L-5.499999999999997 29.491524206117255 A30 30 0 0 1 -16.964569457146705 24.742744050198738 L-16.964569457146705 24.742744050198738 L-22.621423706639085 30.399598299691117 L-30.399598299691117 22.621423706639092 L-24.742744050198738 16.964569457146712 A30 30 0 0 1 -29.491524206117255 5.500000000000009 L-29.491524206117255 5.500000000000009 L-37.491524206117255 5.50000000000001 L-37.491524206117255 -5.500000000000001 L-29.491524206117255 -5.500000000000002 A30 30 0 0 1 -24.742744050198738 -16.964569457146705 L-24.742744050198738 -16.964569457146705 L-30.399598299691117 -22.621423706639085 L-22.621423706639092 -30.399598299691117 L-16.964569457146712 -24.742744050198738 A30 30 0 0 1 -5.500000000000011 -29.491524206117255 L-5.500000000000011 -29.491524206117255 L-5.500000000000012 -37.491524206117255 L5.499999999999998 -37.491524206117255 L5.5 -29.491524206117255 A30 30 0 0 1 16.964569457146702 -24.74274405019874 L16.964569457146702 -24.74274405019874 L22.62142370663908 -30.39959829969112 L30.399598299691117 -22.6214237066391 L24.742744050198738 -16.964569457146716 A30 30 0 0 1 29.491524206117255 -5.500000000000013 M0 -20A20 20 0 1 0 0 20 A20 20 0 1 0 0 -20"
                    fill="#ffffff"
                  ></path>
                </g>
              </g>
            </svg>
          </div>
        )}
        {appState === "sms-start" && (
          <div
            style={{
              marginLeft: "auto",
              marginRight: "auto",
              textAlign: "center",
            }}
          >
            <p style={{ color: "white", margin: "50px", textAlign: "center" }}>
              Freede will be reaching out shortly! You can close this window.
            </p>
          </div>
        )}
        {appState === "error" && (
          <p style={{ color: "white", margin: "50px" }}>{error}</p>
        )}
        {appState === "initial-error" && (
          <div
            style={{
              marginLeft: "auto",
              marginRight: "auto",
              textAlign: "center",
            }}
          >
            <p style={{ color: "white", margin: "50px", textAlign: "center" }}>
              {error}
            </p>
            <SendButton onClick={channel === "sms" ? kickstart : startChat}>
              Try again
            </SendButton>
          </div>
        )}
        {appState === "disclaimer" && (
          <>
            <OptIn
              mode={channel}
              cancel={cancelChat}
              start={startChat}
              number={phone}
              kickstart={kickstart}
            />
          </>
        )}
        {appState === "chat" && (
          <>
            <MessagesContainer ref={messagesContainerRef}>
              <Messages messages={messages} />
              {sending && (
                <div className="typingIndicatorContainer">
                  <div className="typingIndicatorBubble">
                    <div className="typingIndicatorBubbleDot"></div>
                    <div className="typingIndicatorBubbleDot"></div>
                    <div className="typingIndicatorBubbleDot"></div>
                  </div>
                </div>
              )}
            </MessagesContainer>
            <MessageInputContainer disabled={sending}>
              <Input
                ref={inputRef}
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder="Type a message..."
                disabled={sending}
                onKeyUp={handleInput}
              />
              <SendButton onClick={sendMessage}>Send</SendButton>
            </MessageInputContainer>
          </>
        )}
      </ChatContainer>
    </div>
  );
}

export default App;
